/**
 * Subscribe Page Component
 * 
 * 3-step checkout flow for subscription.
 * Public page (but requires authentication for payment).
 */

import React, { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { PublicLayout } from "../components/public/PublicLayout";
import { useAuth } from "../hooks/useAuth";
import { getQueryParam, preserveQueryParams } from "../utils/urlParams";
import {
  createPaymentIntent,
  submitTransaction,
  getPaymentIntent,
  getPlans,
  type SubscribeRequest,
} from "../services/billingApi";
import { updateUserProfile, type UpdateUserProfileRequest } from "../services/userProfileApi";
import { login } from "../services/authApi";
import { logger } from "../services/logger";
import "./subscribe-page.css";

type BillingCycle = "monthly" | "yearly";
type PaymentMethod = "crypto" | "visa";
type PaymentStatus = "idle" | "pending" | "verified" | "rejected";
type CheckoutStep = 1 | 2 | 3 | 4;

function formatNetworkLabel(network: string): string {
  const net = network.toLowerCase();
  if (net === "bsc" || net === "binance-smart-chain" || net === "bep20") {
    return "BEP-20";
  }
  if (net === "polygon") return "Polygon";
  if (net === "arbitrum") return "Arbitrum";
  if (net === "optimism") return "Optimism";
  return "ERC-20";
}

function formatNetworkDisplayName(network: string): string {
  const net = network.toLowerCase();
  if (net === "bsc" || net === "binance-smart-chain" || net === "bep20") {
    return "Binance Smart Chain (BEP-20)";
  }
  if (net === "polygon") return "Polygon (ERC-20)";
  if (net === "arbitrum") return "Arbitrum (ERC-20)";
  if (net === "optimism") return "Optimism (ERC-20)";
  return "Ethereum (ERC-20)";
}

interface PaymentIntentData {
  id: string;
  wallet_address: string;
  network: string;
  token_contract?: string;
  expected_amount: string;
  expires_at: string;
  instructions?: string;
}

export function SubscribePage(): JSX.Element {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [currentStep, setCurrentStep] = useState<CheckoutStep>(1);
  const [planCode, setPlanCode] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("crypto");
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [cryptoAssets, setCryptoAssets] = useState<Array<{ id: string; symbol: string; network: string; decimals: number }>>([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [paymentIntent, setPaymentIntent] = useState<PaymentIntentData | null>(null);
  const [txHash, setTxHash] = useState("");
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);
  const [hasAttemptedCreate, setHasAttemptedCreate] = useState(false);

  // Profile fields
  const [profileName, setProfileName] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [profileCountry, setProfileCountry] = useState("");
  const [profileCity, setProfileCity] = useState("");
  const [profileAddress, setProfileAddress] = useState("");
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Read plan, cycle, and message from URL params
  useEffect(() => {
    const planParam = getQueryParam("plan");
    const cycleParam = getQueryParam("cycle");
    const messageParam = getQueryParam("message");

    if (planParam && (planParam === "STANDARD" || planParam === "PRO")) {
      setPlanCode(planParam);
    }

    if (cycleParam === "monthly" || cycleParam === "yearly") {
      setBillingCycle(cycleParam);
    }

    // Show message if subscription required
    // Store in a separate state to persist even if planCode is missing
    if (messageParam === "subscription_required") {
      setError("Please complete your subscription to access the trading platform. Select a plan below to continue.");
    }
  }, []);

  // Redirect to pricing if plan invalid (but preserve error message)
  // Only redirect if we have no planCode AND no error message set
  // This prevents redirecting away before error message is displayed
  useEffect(() => {
    if (!authLoading && !planCode && !error) {
      // Check if we have subscription_required message in URL
      const messageParam = getQueryParam("message");
      const errorMessage = messageParam === "subscription_required" ? "?message=subscription_required" : "";
      // Only redirect if no error message (to prevent losing error)
      if (!messageParam || messageParam !== "subscription_required") {
        window.location.href = `/pricing${errorMessage}`;
      }
    }
  }, [planCode, authLoading, error]);

  // Load crypto assets when authenticated (load early for better UX)
  useEffect(() => {
    const loadAssets = async () => {
      if (isAuthenticated && cryptoAssets.length === 0 && !isLoadingAssets) {
        setIsLoadingAssets(true);
        try {
          const response = await getPlans();
          const assets = response.payment_methods.crypto.assets || [];
          setCryptoAssets(assets);
          // Auto-select first asset if available
          if (assets.length > 0 && !selectedAssetId) {
            setSelectedAssetId(assets[0].id);
          }
        } catch (err) {
          logger.error("Failed to load crypto assets", err instanceof Error ? err : new Error(String(err)));
        } finally {
          setIsLoadingAssets(false);
        }
      }
    };

    void loadAssets();
  }, [isAuthenticated, cryptoAssets.length, isLoadingAssets, selectedAssetId]);

  // Check authentication IMMEDIATELY - MUST be authenticated to access subscribe page
  // BUT: Don't redirect if we're coming from auth callback with subscription_required message
  // This allows the error message to be displayed before redirecting
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      // Check if we have subscription_required message in URL (means user just logged in but has no subscription)
      const messageParam = getQueryParam("message");
      // If we have subscription_required message, user IS authenticated but just needs subscription
      // Don't redirect to register/login in this case - let the subscription message show
      if (messageParam === "subscription_required") {
        return;
      }
      
      // User MUST login/register first before accessing subscribe page
      const returnTo = preserveQueryParams("/subscribe", {
        plan: planCode || "",
        cycle: billingCycle,
      });
      // Redirect immediately to register (which will show login option) to create account first
      window.location.replace(`/auth/register?returnTo=${encodeURIComponent(returnTo)}`);
    }
  }, [isAuthenticated, authLoading, planCode, billingCycle]);

  // Create payment intent when moving to step 4 (payment step)
  useEffect(() => {
    const createIntent = async () => {
      if (
        currentStep === 4 &&
        isAuthenticated &&
        planCode &&
        paymentMethod === "crypto" &&
        selectedAssetId &&
        !paymentIntent &&
        !isCreatingIntent &&
        !hasAttemptedCreate
      ) {
        // Wait for assets to load if still loading
        if (isLoadingAssets) {
          logger.info("Waiting for crypto assets to load...");
          return;
        }

        // Ensure crypto assets are loaded
        if (cryptoAssets.length === 0) {
          logger.warn("Crypto assets not loaded yet, attempting to load...");
          setIsLoadingAssets(true);
          try {
            const response = await getPlans();
            const assets = response.payment_methods.crypto.assets || [];
            setCryptoAssets(assets);
            if (assets.length === 0) {
              setError("No cryptocurrencies available. Please try again later.");
              setIsLoadingAssets(false);
              return;
            }
            // Re-select asset if it was cleared
            if (!selectedAssetId && assets.length > 0) {
              setSelectedAssetId(assets[0].id);
            }
          } catch (err) {
            logger.error("Failed to load crypto assets", err instanceof Error ? err : new Error(String(err)));
            setError("Failed to load available cryptocurrencies. Please try again.");
            setIsLoadingAssets(false);
            return;
          } finally {
            setIsLoadingAssets(false);
          }
        }

        setIsCreatingIntent(true);
        setHasAttemptedCreate(true);
        setError(null);

        try {
          // Validate all required fields
          if (!planCode) {
            setError("Plan code is missing. Please go back and select a plan.");
            setIsCreatingIntent(false);
            return;
          }

          if (!selectedAssetId || selectedAssetId.trim() === "") {
            setError("Please select a cryptocurrency before continuing.");
            setIsCreatingIntent(false);
            return;
          }

          // Validate asset_id exists in available assets
          const selectedAsset = cryptoAssets.find(asset => asset.id === selectedAssetId);
          if (!selectedAsset) {
            setError("Selected cryptocurrency is no longer available. Please go back and select another one.");
            setIsCreatingIntent(false);
            return;
          }

          // Log request for debugging
          logger.info(
            `Creating payment intent: plan_code=${planCode}, period=${billingCycle}, ` +
            `method=crypto, asset_id=${selectedAssetId}`
          );

          const request: SubscribeRequest = {
            plan_code: planCode,
            period: billingCycle,
            method: "crypto",
            asset_id: selectedAssetId,
          };

          const response = await createPaymentIntent(request);
          setPaymentIntent({
            id: response.intent.id,
            wallet_address: response.intent.wallet_address || "",
            network: response.intent.network || "ethereum",
            token_contract: response.intent.token_contract,
            expected_amount: response.intent.expected_amount || "0",
            expires_at: response.intent.expires_at,
            instructions: response.intent.instructions,
          });
        } catch (err) {
          let errorMessage = "Failed to create payment intent";
          
          if (err instanceof Error) {
            // Check if it's an Axios error with response data
            const axiosError = err as any;
            if (axiosError.response?.data?.detail) {
              errorMessage = axiosError.response.data.detail;
            } else if (err.message.includes("asset_id is required") || err.message.includes("400")) {
              errorMessage = "Please select a cryptocurrency before continuing.";
            } else if (err.message.includes("not found") || err.message.includes("404")) {
              errorMessage = "Plan or cryptocurrency not found. Please go back and try again.";
            } else if (err.message.includes("disabled") || err.message.includes("503")) {
              errorMessage = "Crypto payments are currently disabled. Please try again later.";
            } else {
              errorMessage = err.message || errorMessage;
            }
          }
          
          setError(errorMessage);
          logger.error(
            "Failed to create payment intent",
            err instanceof Error ? err : new Error(String(err)),
            {
              planCode,
              billingCycle,
              selectedAssetId,
              cryptoAssetsCount: cryptoAssets.length,
            }
          );
          // Don't reset hasAttemptedCreate - prevent infinite retry loop
        } finally {
          setIsCreatingIntent(false);
        }
      }
    };

    void createIntent();
  }, [
    currentStep,
    isAuthenticated,
    planCode,
    billingCycle,
    paymentMethod,
    selectedAssetId,
    paymentIntent,
    isCreatingIntent,
    hasAttemptedCreate,
    cryptoAssets,
    isLoadingAssets,
  ]);

  // Reset hasAttemptedCreate when going back to step 3 or changing asset
  useEffect(() => {
    if (currentStep < 4 || !selectedAssetId) {
      setHasAttemptedCreate(false);
      setPaymentIntent(null);
    }
  }, [currentStep, selectedAssetId]);

  // Poll payment intent status if tx_hash submitted
  useEffect(() => {
    if (
      paymentIntent &&
      txHash &&
      paymentStatus === "pending" &&
      isAuthenticated
    ) {
      const pollStatus = async () => {
        try {
          const intent = await getPaymentIntent(paymentIntent.id);
          if (intent.status === "verified") {
            setPaymentStatus("verified");
          } else if (intent.status === "rejected") {
            setPaymentStatus("rejected");
            setError(intent.reason || "Payment verification failed");
          }
        } catch (err) {
          logger.error("Error polling payment intent status", err instanceof Error ? err : new Error(String(err)));
        }
      };

      const interval = setInterval(pollStatus, 5000);
      return () => clearInterval(interval);
    }
  }, [paymentIntent, txHash, paymentStatus, isAuthenticated]);

  // Auto-redirect when payment is verified (after 5 seconds to show confirmation)
  useEffect(() => {
    if (paymentStatus === "verified") {
      const redirectTimer = setTimeout(() => {
        window.location.href = "/app?tab=integrations";
      }, 5000);
      return () => clearTimeout(redirectTimer);
    }
  }, [paymentStatus]);

  const handleStep1Next = () => {
    if (!planCode) {
      setError("Please select a plan");
      return;
    }
    if (!termsAccepted) {
      setError("Please accept the Terms of Service and Risk Disclaimer to continue.");
      return;
    }
    setCurrentStep(2);
    setError(null);
  };

  const handleStep2Next = async () => {
    if (!profileSaved) {
      setIsSavingProfile(true);
      setError(null);
      try {
        const profileData: UpdateUserProfileRequest = {};
        if (profileName.trim()) profileData.name = profileName.trim();
        if (profilePhone.trim()) profileData.phone = profilePhone.trim();
        if (profileCountry.trim()) profileData.country = profileCountry.trim();
        if (profileCity.trim()) profileData.city = profileCity.trim();
        if (profileAddress.trim()) profileData.address = profileAddress.trim();
        await updateUserProfile(profileData);
        setProfileSaved(true);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to save profile";
        setError(errorMessage);
        logger.error("Failed to save user profile", err instanceof Error ? err : new Error(String(err)));
        setIsSavingProfile(false);
        return;
      } finally {
        setIsSavingProfile(false);
      }
    }
    setCurrentStep(3);
    setError(null);
  };

  const handleStep3Next = () => {
    if (paymentMethod === "visa") {
      setError("Visa payment is coming soon");
      return;
    }
    if (paymentMethod === "crypto") {
      if (!selectedAssetId) {
        setError("Please select a cryptocurrency");
        return;
      }
      const assetExists = cryptoAssets.some(asset => asset.id === selectedAssetId);
      if (!assetExists) {
        setError("Selected cryptocurrency is no longer available. Please select another one.");
        return;
      }
    }
    setCurrentStep(4);
    setError(null);
  };

  const handleTxHashSubmit = async () => {
    if (!txHash.trim()) {
      setError("Please enter a transaction hash");
      return;
    }

    // Validate tx_hash format (hex string, 64 characters)
    const txHashRegex = /^0x[a-fA-F0-9]{64}$/;
    if (!txHashRegex.test(txHash.trim())) {
      setError("Invalid transaction hash format. Must be a 64-character hex string starting with 0x.");
      return;
    }

    if (!paymentIntent) {
      setError("Payment intent not created. Please refresh the page.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await submitTransaction(paymentIntent.id, txHash.trim());
      setPaymentStatus("pending");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to submit transaction";
      
      // Handle specific error cases
      if (errorMessage.includes("already used") || errorMessage.includes("duplicate")) {
        setError("Already used: This transaction hash was already used.");
      } else if (errorMessage.includes("expired")) {
        setError("Payment request expired. Please create a new one.");
      } else if (errorMessage.includes("not found") || errorMessage.includes("couldn't find")) {
        setError("Not found: We couldn't find this transaction. Check the hash and network.");
      } else if (errorMessage.includes("wrong network") || errorMessage.includes("not on Ethereum")) {
        setError("Wrong network: Transaction is not on Ethereum.");
      } else if (errorMessage.includes("underpaid") || errorMessage.includes("amount too low")) {
        setError("Underpaid: Amount received is lower than required. Please contact support.");
      } else {
        setError(errorMessage);
      }
          logger.error("Failed to submit transaction", err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinueToLogin = () => {
    const returnTo = preserveQueryParams("/subscribe", {
      plan: planCode || "",
      cycle: billingCycle,
    });
    login(returnTo);
  };

  const getPlanDisplayName = (code: string): string => {
    if (code === "STANDARD") return "Pro";
    if (code === "PRO") return "Ultra";
    return code;
  };

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <PublicLayout>
        <div className="subscribe-page">
          <div className="subscribe-page__loading">Loading...</div>
        </div>
      </PublicLayout>
    );
  }

  // Redirect happens in useEffect - show loading while redirecting
  if (!isAuthenticated) {
    return (
      <PublicLayout>
        <div className="subscribe-page">
          <div className="subscribe-page__loading">Redirecting to registration...</div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="subscribe-page">
        <div className="subscribe-page__container">
          {/* Stepper */}
          <div className="subscribe-stepper">
            <div
              className={`subscribe-stepper__step ${
                currentStep >= 1 ? "subscribe-stepper__step--active" : ""
              } ${currentStep > 1 ? "subscribe-stepper__step--completed" : ""}`}
            >
              <div className="subscribe-stepper__step-number">1</div>
              <div className="subscribe-stepper__step-label">Choose your plan</div>
            </div>
            <div className="subscribe-stepper__connector" />
            <div
              className={`subscribe-stepper__step ${
                currentStep >= 2 ? "subscribe-stepper__step--active" : ""
              } ${currentStep > 2 ? "subscribe-stepper__step--completed" : ""}`}
            >
              <div className="subscribe-stepper__step-number">2</div>
              <div className="subscribe-stepper__step-label">Your profile</div>
            </div>
            <div className="subscribe-stepper__connector" />
            <div
              className={`subscribe-stepper__step ${
                currentStep >= 3 ? "subscribe-stepper__step--active" : ""
              } ${currentStep > 3 ? "subscribe-stepper__step--completed" : ""}`}
            >
              <div className="subscribe-stepper__step-number">3</div>
              <div className="subscribe-stepper__step-label">Select payment method</div>
            </div>
            <div className="subscribe-stepper__connector" />
            <div
              className={`subscribe-stepper__step ${
                currentStep >= 4 ? "subscribe-stepper__step--active" : ""
              }`}
            >
              <div className="subscribe-stepper__step-number">4</div>
              <div className="subscribe-stepper__step-label">Complete payment</div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="subscribe-page__error" role="alert">
              {error}
            </div>
          )}

          {/* Step 1: Choose Plan */}
          {currentStep === 1 && (
            <div className="subscribe-step">
              <h2 className="subscribe-step__title">Choose your plan</h2>
              <p className="subscribe-step__subtitle">
                Select Pro or Ultra, then choose monthly or yearly.
              </p>
              <p className="subscribe-step__subtitle">
                Trading is executed on Bybit only. You will need a Bybit account to use automated trading.
              </p>
              <div className="subscribe-step__content">
                <div className="subscribe-plan-summary">
                  <div className="subscribe-plan-summary__item">
                    <span className="subscribe-plan-summary__label">Plan:</span>
                    <span className="subscribe-plan-summary__value">
                      {planCode ? getPlanDisplayName(planCode) : "Not selected"}
                    </span>
                  </div>
                  <div className="subscribe-plan-summary__item">
                    <span className="subscribe-plan-summary__label">Billing Cycle:</span>
                    <span className="subscribe-plan-summary__value">
                      {billingCycle === "monthly" ? "Monthly" : "Yearly"}
                    </span>
                  </div>
                </div>
                <label
                  className="subscribe-terms-consent"
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "0.5rem",
                    fontSize: "0.85rem",
                    margin: "1rem 0",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(event) => setTermsAccepted(event.target.checked)}
                    style={{ marginTop: "0.2rem" }}
                  />
                  <span>
                    I agree to the{" "}
                    <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and{" "}
                    <a href="/risk-disclaimer" target="_blank" rel="noopener noreferrer">Risk Disclaimer</a>,
                    and I understand that automated trading may be executed on my connected exchange account.
                  </span>
                </label>
                <div className="subscribe-step__actions">
                  <a
                    href="/pricing"
                    className="subscribe-step__button subscribe-step__button--secondary"
                  >
                    Change Plan
                  </a>
                  <button
                    type="button"
                    className="subscribe-step__button subscribe-step__button--primary"
                    onClick={handleStep1Next}
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Profile Information */}
          {currentStep === 2 && (
            <div className="subscribe-step">
              <h2 className="subscribe-step__title">Your profile</h2>
              <p className="subscribe-step__subtitle">
                Please fill in your details before continuing.
              </p>
              <div className="subscribe-step__content">
                <form
                  className="subscribe-profile-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void handleStep2Next();
                  }}
                >
                  <div className="subscribe-profile-form__field">
                    <label htmlFor="profile-name" className="subscribe-profile-form__label">
                      Full name
                    </label>
                    <input
                      id="profile-name"
                      type="text"
                      className="subscribe-profile-form__input"
                      value={profileName}
                      onChange={(e) => setProfileName(e.target.value)}
                      placeholder="Enter your full name"
                      maxLength={255}
                    />
                  </div>
                  <div className="subscribe-profile-form__field">
                    <label htmlFor="profile-phone" className="subscribe-profile-form__label">
                      Phone number
                    </label>
                    <input
                      id="profile-phone"
                      type="tel"
                      className="subscribe-profile-form__input"
                      value={profilePhone}
                      onChange={(e) => setProfilePhone(e.target.value)}
                      placeholder="+1 234 567 890"
                      maxLength={32}
                    />
                  </div>
                  <div className="subscribe-profile-form__row">
                    <div className="subscribe-profile-form__field">
                      <label htmlFor="profile-country" className="subscribe-profile-form__label">
                        Country
                      </label>
                      <input
                        id="profile-country"
                        type="text"
                        className="subscribe-profile-form__input"
                        value={profileCountry}
                        onChange={(e) => setProfileCountry(e.target.value)}
                        placeholder="Your country"
                        maxLength={128}
                      />
                    </div>
                    <div className="subscribe-profile-form__field">
                      <label htmlFor="profile-city" className="subscribe-profile-form__label">
                        City
                      </label>
                      <input
                        id="profile-city"
                        type="text"
                        className="subscribe-profile-form__input"
                        value={profileCity}
                        onChange={(e) => setProfileCity(e.target.value)}
                        placeholder="Your city"
                        maxLength={128}
                      />
                    </div>
                  </div>
                  <div className="subscribe-profile-form__field">
                    <label htmlFor="profile-address" className="subscribe-profile-form__label">
                      Address (optional)
                    </label>
                    <input
                      id="profile-address"
                      type="text"
                      className="subscribe-profile-form__input"
                      value={profileAddress}
                      onChange={(e) => setProfileAddress(e.target.value)}
                      placeholder="Street address"
                      maxLength={512}
                    />
                  </div>
                  <div className="subscribe-step__actions">
                    <button
                      type="button"
                      className="subscribe-step__button subscribe-step__button--secondary"
                      onClick={() => {
                        setCurrentStep(1);
                        setError(null);
                      }}
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="subscribe-step__button subscribe-step__button--primary"
                      disabled={isSavingProfile}
                    >
                      {isSavingProfile ? "Saving..." : "Continue to Payment"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Step 3: Payment Method */}
          {currentStep === 3 && (
            <div className="subscribe-step">
              <h2 className="subscribe-step__title">Payment method</h2>
              <p className="subscribe-step__subtitle">
                Crypto
              </p>
              <div className="subscribe-step__content">
                <div className="subscribe-payment-methods">
                  <button
                    type="button"
                    className={`subscribe-payment-method ${
                      paymentMethod === "crypto"
                        ? "subscribe-payment-method--selected"
                        : ""
                    }`}
                    onClick={() => {
                      setPaymentMethod("crypto");
                      // Auto-select first asset if available, otherwise set to null
                      if (cryptoAssets.length > 0) {
                        setSelectedAssetId(cryptoAssets[0].id);
                      } else {
                        setSelectedAssetId(null);
                      }
                      setHasAttemptedCreate(false);
                      setPaymentIntent(null);
                    }}
                  >
                    <div className="subscribe-payment-method__header">
                      <span className="subscribe-payment-method__name">Crypto</span>
                      <span className="subscribe-payment-method__badge subscribe-payment-method__badge--enabled">
                        Enabled
                      </span>
                    </div>
                    <p className="subscribe-payment-method__description">
                      Pay with crypto (BEP20 / ERC-20)
                    </p>
                  </button>

                  <button
                    type="button"
                    className={`subscribe-payment-method ${
                      paymentMethod === "visa"
                        ? "subscribe-payment-method--selected"
                        : ""
                    }`}
                    onClick={() => setPaymentMethod("visa")}
                    disabled
                  >
                    <div className="subscribe-payment-method__header">
                      <span className="subscribe-payment-method__name">Visa</span>
                      <span className="subscribe-payment-method__badge subscribe-payment-method__badge--coming-soon">
                        Coming soon
                      </span>
                    </div>
                    <p className="subscribe-payment-method__description">
                      Card payments will be available soon
                    </p>
                  </button>
                </div>

                <div className="subscribe-payment-info">
                  <p className="subscribe-payment-info__text">
                    You will send the exact amount to the address shown. Make sure to select the correct network.
                  </p>
                </div>

                {/* Crypto Asset Selector */}
                {paymentMethod === "crypto" && (
                  <div className="subscribe-crypto-assets">
                    <label className="subscribe-crypto-assets__label">
                      Select cryptocurrency:
                    </label>
                    {isLoadingAssets ? (
                      <div className="subscribe-crypto-assets__loading">
                        <span className="subscribe-crypto-assets__loading-spinner">⏳</span>
                        Loading available cryptocurrencies...
                      </div>
                    ) : cryptoAssets.length > 0 ? (
                      <div className="subscribe-crypto-assets__list">
                        {cryptoAssets.map((asset) => (
                          <button
                            key={asset.id}
                            type="button"
                            className={`subscribe-crypto-asset ${
                              selectedAssetId === asset.id
                                ? "subscribe-crypto-asset--selected"
                                : ""
                            }`}
                            onClick={() => {
                              setSelectedAssetId(asset.id);
                              setHasAttemptedCreate(false);
                              setPaymentIntent(null);
                              setError(null);
                            }}
                            aria-pressed={selectedAssetId === asset.id}
                            aria-label={`Select ${asset.symbol} for payment`}
                          >
                            <span className="subscribe-crypto-asset__symbol">{asset.symbol}</span>
                            <span className="subscribe-crypto-asset__network">({formatNetworkLabel(asset.network)})</span>
                            {selectedAssetId === asset.id && (
                              <span className="subscribe-crypto-asset__checkmark">✓</span>
                            )}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="subscribe-crypto-assets__error">
                        No crypto assets available. Please contact support.
                      </div>
                    )}
                  </div>
                )}

                <div className="subscribe-step__actions">
                  <button
                    type="button"
                    className="subscribe-step__button subscribe-step__button--secondary"
                    onClick={() => setCurrentStep(2)}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="subscribe-step__button subscribe-step__button--primary"
                    onClick={handleStep3Next}
                    disabled={paymentMethod === "crypto" && !selectedAssetId}
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Crypto Payment */}
          {currentStep === 4 && paymentMethod === "crypto" && (
            <div className="subscribe-step">
              <h2 className="subscribe-step__title">Complete your payment</h2>
              <div className="subscribe-step__content">
                {isCreatingIntent ? (
                  <div className="subscribe-step__loading">Creating payment intent...</div>
                ) : paymentIntent ? (
                  <>
                    {/* Payment Instructions */}
                    <div className="subscribe-crypto-instructions">
                      <h3 className="subscribe-crypto-instructions__title">
                        Complete your payment
                      </h3>
                      <ol className="subscribe-crypto-instructions__steps">
                        <li>Select the correct network and asset above</li>
                        <li>Send the exact amount to the address below ({paymentIntent.network ? formatNetworkDisplayName(paymentIntent.network) : ""} network)</li>
                        <li>Important: Account for transfer fees when sending. The full amount must arrive at the destination wallet.</li>
                        <li>Copy the Transaction Hash (TXID) and paste it here</li>
                        <li>Click Verify payment</li>
                      </ol>
                      
                      {/* Transfer Fee Warning */}
                      <div className="subscribe-crypto-warning" style={{
                        marginTop: "1rem",
                        padding: "1rem",
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffc107",
                        borderRadius: "4px",
                        color: "#856404"
                      }}>
                        <strong>⚠️ Important:</strong> When transferring funds, please account for network transaction fees. 
                        The full amount ({paymentIntent.expected_amount} {cryptoAssets.find(a => a.id === selectedAssetId)?.symbol || "USD"}) must arrive at the destination wallet. 
                        If you send from an exchange, withdrawal fees will be deducted from your balance, so ensure you send enough to cover both the payment amount and fees.
                      </div>

                      {/* Wallet Address */}
                      <div className="subscribe-crypto-details">
                        {paymentIntent.wallet_address && (
                          <div className="subscribe-crypto-qr">
                            <QRCodeSVG
                              value={paymentIntent.wallet_address}
                              size={160}
                              bgColor="#ffffff"
                              fgColor="#000000"
                              level="M"
                              includeMargin={true}
                            />
                          </div>
                        )}
                        <div className="subscribe-crypto-detail">
                          <span className="subscribe-crypto-detail__label">Wallet Address:</span>
                          <code className="subscribe-crypto-detail__value">
                            {paymentIntent.wallet_address}
                          </code>
                          <button
                            type="button"
                            className="subscribe-crypto-detail__copy"
                            onClick={() => {
                              navigator.clipboard.writeText(paymentIntent.wallet_address);
                            }}
                            aria-label="Copy wallet address"
                          >
                            Copy
                          </button>
                        </div>
                        <div className="subscribe-crypto-detail">
                          <span className="subscribe-crypto-detail__label">Network:</span>
                          <span className="subscribe-crypto-detail__value">
                            {formatNetworkDisplayName(paymentIntent.network || "")}
                          </span>
                        </div>
                        <div className="subscribe-crypto-detail">
                          <span className="subscribe-crypto-detail__label">Amount:</span>
                          <span className="subscribe-crypto-detail__value">
                            {paymentIntent.expected_amount} {cryptoAssets.find(a => a.id === selectedAssetId)?.symbol || "USD"}
                          </span>
                        </div>
                        <div className="subscribe-crypto-detail">
                          <span className="subscribe-crypto-detail__label">Expires:</span>
                          <span className="subscribe-crypto-detail__value">
                            {new Date(paymentIntent.expires_at).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Transaction Hash Input */}
                    {paymentStatus !== "verified" && (
                      <div className="subscribe-tx-input">
                        <label
                          htmlFor="tx-hash"
                          className="subscribe-tx-input__label"
                        >
                          Transaction Hash (tx_hash):
                        </label>
                        <div className="subscribe-tx-input__group">
                          <input
                            id="tx-hash"
                            type="text"
                            className="subscribe-tx-input__field"
                            value={txHash}
                            onChange={(e) => setTxHash(e.target.value)}
                            placeholder="0x..."
                            disabled={isSubmitting || paymentStatus === "pending"}
                          />
                          <button
                            type="button"
                            className="subscribe-tx-input__submit"
                            onClick={handleTxHashSubmit}
                            disabled={
                              !txHash.trim() ||
                              isSubmitting ||
                              paymentStatus === "pending"
                            }
                          >
                            {isSubmitting ? "Submitting..." : "Verify payment"}
                          </button>
                        </div>
                        <p className="subscribe-tx-input__hint">
                          Verification usually completes after 12 confirmations.
                        </p>
                      </div>
                    )}

                    {/* Payment Status */}
                    {paymentStatus === "pending" && (
                      <div className="subscribe-payment-status subscribe-payment-status--pending">
                        <div className="subscribe-payment-status__icon">⏳</div>
                        <div className="subscribe-payment-status__message">
                          Pending: Transaction found. Waiting for confirmations.
                        </div>
                      </div>
                    )}

                    {paymentStatus === "verified" && (
                      <>
                        <div className="subscribe-payment-status subscribe-payment-status--verified">
                          <div className="subscribe-payment-status__icon">✅</div>
                          <div className="subscribe-payment-status__message">
                            Verified: Payment confirmed. Your subscription is active now.
                          </div>
                        </div>
                        <div className="subscribe-renewal-note">
                          <p className="subscribe-renewal-note__text">
                            Renewals extend from your current end date. You won't lose remaining time.
                          </p>
                        </div>
                      </>
                    )}

                    {paymentStatus === "rejected" && (
                      <div className="subscribe-payment-status subscribe-payment-status--rejected">
                        <div className="subscribe-payment-status__icon">❌</div>
                        <div className="subscribe-payment-status__message">
                          {error || "Rejected: Payment could not be verified. Please check the reason above."}
                        </div>
                      </div>
                    )}

                    <div className="subscribe-step__actions">
                      {paymentStatus === "verified" ? (
                        <>
                          <button
                            type="button"
                            className="subscribe-step__button subscribe-step__button--secondary"
                            onClick={() => setCurrentStep(3)}
                          >
                            Back
                          </button>
                          <button
                            type="button"
                            className="subscribe-step__button subscribe-step__button--primary"
                            onClick={() => {
                              // Redirect to integrations setup after successful payment
                              window.location.href = "/app?tab=integrations";
                            }}
                          >
                            Continue to Dashboard
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="subscribe-step__button subscribe-step__button--secondary"
                          onClick={() => setCurrentStep(3)}
                          disabled={paymentStatus === "pending"}
                        >
                          Back
                        </button>
                      )}
                    </div>
                  </>
                ) : error ? (
                  <div className="subscribe-step__error">
                    <p>{error}</p>
                    <button
                      type="button"
                      className="subscribe-step__button subscribe-step__button--primary"
                      onClick={() => {
                        setHasAttemptedCreate(false);
                        setError(null);
                      }}
                    >
                      Retry
                    </button>
                  </div>
                ) : (
                  <div className="subscribe-step__error">
                    Failed to create payment intent. Please try again.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}

export default SubscribePage;

